import { Injectable } from '@angular/core';
import { Accounts, Commands, Filters, Results, Search } from '@local/client-contracts';
import { getExtensionByFileName, getIconByExtension } from '@local/ts-infra';
import { DateFormat } from '@shared/consts';
import { DragMetadata } from '@shared/directives/drag-and-drop.directive';
import { AccountsService } from '@shared/services/accounts.service';
import { randomColor } from '@shared/utils';
import { getDateFormat } from '@shared/utils/date-format.util';
import { getDownloadIcon } from '@shared/utils/set-icon.util';
import moment from 'moment';
import { FiltersService } from 'src/app/bar/services/filters.service';
import { isAttachment } from '../../results/utils/results.util';

export type Recipient = { name: string; mail: string; tooltip: string };
export type Profile = { url?: string; color?: string };

export interface MailViewModel {
  isStarred: boolean;
  isUnread: boolean;
  isSentItem: boolean;
  isRestricted: boolean;
  hasAttachment: boolean;
  hasCalendarAttachment: boolean;
  sender: Results.Title;
  senderEmail: string;
  dragMetadata?: DragMetadata;
  creationTime?: string;
  title?: Results.Title;
  subtitle?: Results.Subtitle;
  labels?: Results.Tag[];
  attachments?: Results.Attachment[];
  recipients?: Recipient[];
  icon?: string;
}

@Injectable()
export class MailBuildResultViewService {
  private preFilters: Filters.Values;
  private workspaceAccounts: Accounts.WorkspaceAccount[];
  private readonly calendarSvg = '/assets/file-icons/calendar.svg';

  private viewModel: MailViewModel = {
    hasAttachment: false,
    hasCalendarAttachment: false,
    isRestricted: false,
    isSentItem: false,
    isStarred: false,
    isUnread: false,
    sender: { text: '' },
    senderEmail: '',
    attachments: [],
    labels: [],
    recipients: [],
  };

  constructor(private filtersService: FiltersService, private accountsService: AccountsService) {
    this.initWorkspaceAccounts();
  }

  buildResultView(model: Search.ResultResourceItem, inboxFilterOnly = false, isPreview?: boolean): MailViewModel {
    let viewModel = this.viewModel;
    this.preFilters = null;
    viewModel = {
      ...viewModel,
      creationTime: this.setTime(model),
      isUnread: model?.resource?.traits?.labels?.find((label) => label === 'UNREAD'),
      isSentItem: model?.resource?.traits?.labels?.find((label) => label === 'SENT' || label === 'Sent Items'),
      hasAttachment: model?.resource?.traits?.hasAttachments,
      title: model?.view?.title,
      subtitle: model?.view?.subtitle,
      isStarred: model?.resource?.traits?.isStarred,
      isRestricted: model?.link?.shareOptions?.restricted,
      senderEmail: model?.resource?.traits?.sender?.email,
      recipients: model?.resource?.traits?.recipients?.map((r) => {
        return { name: r.name, mail: r.email, tooltip: r.name ? `Click to copy (${r.email})` : 'Click to copy' };
      }),
      dragMetadata: this.buildDragMetaData(model),
    };

    if (viewModel.isSentItem) {
      if (viewModel.recipients.length) {
        let recipientsText = 'To: ';
        viewModel.recipients.forEach((recipient, index) => {
          const name = recipient.name || recipient.mail;
          recipientsText += index === viewModel.recipients.length - 1 ? name : `${name}, `;
        });
        viewModel.sender = { text: recipientsText };
      }
    } else {
      const sender = model?.resource?.traits?.sender;
      viewModel.sender = { text: sender?.name || sender?.email };
    }
    viewModel.sender = {
      ...viewModel.sender,
      tooltip: `Click to copy (${viewModel.senderEmail})`,
      onClick: isPreview ? ({ type: 'copy-clipboard', value: viewModel.senderEmail } as Commands.CopyClipboard) : model?.view.title.onClick,
    };

    viewModel = { ...viewModel, ...this.setLabelsAndAttachments(model, viewModel) };
    this.preFilters = this.filtersService.getPreFilters();
    viewModel = { ...viewModel, ...this.removeLabelsByFilter(inboxFilterOnly, viewModel) };
    return viewModel;
  }

  private setTime(model: Search.ResultResourceItem): string {
    const date: Date = new Date(model?.resource?.traits?.createdAt);
    const timeFormat: DateFormat = getDateFormat(date);
    let format: string;

    switch (timeFormat) {
      case 'today':
        format = 'HH:mm';
        break;
      case 'other':
        format = 'MM/DD/YY';
        break;
      default:
        format = 'MMM DD';
        break;
    }
    return moment(date).format(format);
  }

  buildDragMetaData(model: Search.ResultResourceItem): DragMetadata {
    if (!model) return;
    const icon = getDownloadIcon(model.view?.icon, model.view?.iconOverlay);
    return {
      type: 'Resource',
      resource: model?.resource,
      icon,
    };
  }

  private setLabelsAndAttachments(model: Search.ResultResourceItem, viewModel: MailViewModel): MailViewModel {
    model?.view?.tagGroups?.forEach((group) => {
      if (group.tags.find((attachment: any) => attachment.onDrag)) {
        viewModel.attachments = group.tags?.map((attachment: Results.Tag | Results.Attachment) => {
          let extension: string;
          const text = attachment.text;
          if (isAttachment(attachment)) {
            extension = attachment.extension;
          } else {
            extension = getExtensionByFileName(attachment.text);
          }
          const icon = { lightUrl: getIconByExtension(extension) };
          if (icon.lightUrl === this.calendarSvg) {
            viewModel.hasCalendarAttachment = true;
          }
          if (attachment.icon && (attachment.icon as Results.FontIcon)?.name === 'icon-paperclip') {
            attachment.icon = { lightUrl: '/assets/file-icons/general.svg' };
          }
          return {
            ...attachment,
            text: text,
            tooltip: attachment.text,
            extension: extension,
          };
        });
      } else {
        viewModel.labels = group.tags.map((tag) => {
          if (tag.text.toLowerCase() !== 'starred') {
            return { ...tag, tooltip: tag.text };
          }
        });
        viewModel.labels = viewModel.labels.filter((l) => !!l);
      }
    });
    return viewModel;
  }

  private removeLabelsByFilter(inboxFilterOnly, viewModel: MailViewModel): MailViewModel {
    if (!viewModel.labels || !this.preFilters) return;
    let labelFilter = [];
    if (this.preFilters) {
      const { anyLabel, label } = this.preFilters;
      labelFilter = label?.length > 0 ? label : anyLabel;
    }
    if ((!inboxFilterOnly && labelFilter?.length !== 1) || !labelFilter) return;

    const labelIndex = viewModel.labels?.findIndex((label) => labelFilter?.includes(label.text));
    if (labelIndex !== -1) {
      viewModel.labels.splice(labelIndex, 1);
    }
    return viewModel;
  }

  private initWorkspaceAccounts() {
    this.accountsService.all$.subscribe((value) => {
      this.workspaceAccounts = value;
    });
  }

  buildProfile(item: MailViewModel): Profile {
    const account = this.workspaceAccounts?.find((w) => w.email === item.senderEmail);
    if (account && account.picture) return { url: account.picture };
    return { color: randomColor() };
  }
}
